<template>
  <div class="relative overflow-hidden bg-white rounded-3xl md:rounded-[1.389vw] big-screen:rounded-[1.042vw] py-10 md:py-[2.778vw] big-screen:py-[2.083vw] px-[1.7rem] md:px-[1.389vw] big-screen:px-[1.042vw]">
    <div class="form-input relative">

      <div class="form-input-field">
        <label class="form-input-field-label" for="full-name">Name <span class="text-red-500">*</span></label>
        <input class="form-input-field-input" v-model="inputData.fullName" type="text" name="full-name" id="full-name" placeholder="Enter Full Name" required>
        <TransitionFade>
          <div v-if="errors.fullName" class="form-input-field-error">*This field is required</div>
        </TransitionFade>
      </div>

      <div class="form-input-field">
        <label class="form-input-field-label" for="email">Email<span class="text-red-500">*</span></label>
        <input class="form-input-field-input" v-model="inputData.email" type="email" name="email" id="email" placeholder="Enter Email" required>
        <TransitionFade>
          <div v-if="errors.email" class="form-input-field-error">*{{ errors.email }}</div>
        </TransitionFade>
      </div>

      <div class="form-input-field">
        <label class="form-input-field-label" for="company">Company Name<span class="text-red-500">*</span></label>
        <input class="form-input-field-input" v-model="inputData.company" type="text" name="company" id="company" placeholder="Enter Company Name" required>
        <TransitionFade>
          <div v-if="errors.company" class="form-input-field-error">*This field is required</div>
        </TransitionFade>
      </div>

      <div class="flex flex-col md:flex-row justify-between items-end md:items-end gap-4 w-full">
        <div v-if="!submitted">
          <NuxtTurnstile
            ref="turnstile"
            :options="turnstileOptions"
          />
        </div>
        <div></div>
        <button :disabled="!captchaSuccess" @click="submitData" :class="!captchaSuccess? 'opacity-50':'opacity-100'" class="bg-f-primary-yellow hover:bg-f-primary-blue text-f-primary-blue hover:text-white rounded-3xl py-2 px-4 transition-colors duration-300 flex items-center gap-2">
          <div v-if="!isSubmitting" class="flex justify-center items-center">
            <span class="font-bold">
              Submit
            </span>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-5">
              <path fill-rule="evenodd" d="M5.22 14.78a.75.75 0 0 0 1.06 0l7.22-7.22v5.69a.75.75 0 0 0 1.5 0v-7.5a.75.75 0 0 0-.75-.75h-7.5a.75.75 0 0 0 0 1.5h5.69l-7.22 7.22a.75.75 0 0 0 0 1.06Z" clip-rule="evenodd" />
            </svg>
          </div>
          <div v-else class="flex justify-center items-center gap-2">
            <span class="font-bold">
              Submitting
            </span>
            <svg xmlns="http://www.w3.org/2000/svg" class="size-5" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" stroke-dasharray="16" stroke-dashoffset="16" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3c4.97 0 9 4.03 9 9"><animate fill="freeze" attributeName="stroke-dashoffset" dur="0.2s" values="16;0"/><animateTransform attributeName="transform" dur="1.5s" repeatCount="indefinite" type="rotate" values="0 12 12;360 12 12"/></path></svg>
          </div>
        </button>
      </div>
    </div>
    <div v-if="submitted" class="absolute z-1 w-full h-full top-0 left-0 bg-white flex justify-center items-center">
      <div class="text-center">
        <dotlottie-player src="/lottie/checkmark.lottie" background="transparent" speed="1" class="size-56 md:size-[10vw] mx-auto" autoPlay></dotlottie-player>
        <h2 class="text-2xl font-bold text-f-primary-blue whitespace-pre-line">{{ "You're all set! \nThe file is waiting for you." }}</h2>
        <p class="text-lg text-f-primary-blue mt-4">Please, check your email</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import MakeId from '@/includes/MakeId';
const emits = defineEmits(['onSuccess', 'onError']);

const isSubmitting = ref(false);
const submitted = ref(false);
const captchaSuccess = ref(false);
const inputData = ref({
  fullName: '',
  email: '',
  company: '',
});

const errors = ref({
  fullName: false,
  email: false,
  company: false,
});

const resetInput = () => {
  inputData.value.fullName = '';
  inputData.value.email = '';
  inputData.value.company = '';
};

const resetErrors = () => {
  errors.value.fullName = false;
  errors.value.email = false;
  errors.value.company = false;
};

const turnstile = ref();

const handleErrorCalback = () => {
  captchaSuccess.value = false;
  turnstile.value?.reset();
};
const handleExpiredCallback = () => {
  captchaSuccess.value = false;
  turnstile.value?.reset();
};
const handleLoadCallback = (token) => {
  captchaSuccess.value = true;
  const to = setTimeout(() => {
    handleExpiredCallback();
    clearTimeout(to);
  }, 1000 * 250);
};

const turnstileOptions = ref({
  theme: 'light',
  size: 'normal',
  callback: handleLoadCallback,
  'error-callback': handleErrorCalback,
  'expired-callback': handleExpiredCallback,
});

const validateName = () => {
  if (inputData.value.fullName === '') {
    errors.value.fullName = true;
    return false;
  } else {
    errors.value.fullName = false;
    return true;
  }
};

const validateEmail = () => {
  if (!inputData.value.email) {
    errors.value.email = 'Please enter a valid email address';
    return false;
  }

  const result = inputData.value.email.match(
    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
  );
  if (!result) {
    errors.value.email = 'Please enter a valid email address';
    return false;
  }
  errors.value.email = null;
  return true;
};

const validateCompany = () => {
  if (inputData.value.company === '') {
    errors.value.company = true;
    return false;
  } else {
    errors.value.company = false;
    return true;
  }
};

const submitData = async () => {
  resetErrors();
  // Validate all fields
  const name = validateName();
  const email = validateEmail();
  const company = validateCompany();
  // const jobTitle = validateJobTitle();
  // const phone = validatePhone();

  // if (name && email && company && jobTitle && phone) {
  if (name && email && company) {
    // Submit data
    dataLayer.push({ event: 'Submission' });
    await sendEmail();
    await saveToDatabase();
  } else {
    // console.log('Please fill all fields');
  }
};

const sendEmail = async () => {
  isSubmitting.value = true;
  try {
    await $fetch('/api/send-email', {
      method: 'post',
      body: {
        name: inputData.value.fullName,
        email: inputData.value.email,
        subject: 'ImpacThink Training Modules and Credentials',
      },
    }).then(res => {
      nextTick(() => {
        emits('onSuccess');
        const to = setTimeout(() => { isSubmitting.value = false; submitted.value = true; resetInput(); resetErrors(); clearTimeout(to); }, 500);
        const to2 = setTimeout(() => { submitted.value = false; clearTimeout(to2); }, 4000);
      });
    });
  } catch (error) {
    isSubmitting.value = false;
    emits('onError');
  }
};

const saveToDatabase = async () => {
  inputData.value.id = MakeId(10);
  try {
    const { data: response } = await useAsyncData('response', () => $fetch('/api/submission', {
      method: 'POST',
      body: { action: 'create', input: inputData.value },
      transform: (_response) => _response.error ? _response.error : _response.result,
    }));
    if (response.value.result) {
      // Success saving to database
      // console.log('Data saved to database');
    } else {
      // Error saving to database
      // console.log('Data saved to database');
    }
  } catch (e) {
    // console.log(e.message);
  }
};

</script>
